<script lang="ts" setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMutation, useQueryClient } from '@tanstack/vue-query';

import { AssetContainerType } from '@nova/core';
import { EditableKeyValue } from '@satellite/components/elements/key_value';
import { PRESENTER_VALUE_FALLBACK } from '@satellite/components/elements/presenters/presenterValueFallback';
import { queryKeys } from '@satellite/composables/queryKeys';
import { useAssetContainerTriggersByWarehouse } from '@satellite/composables/useAssetContainerTriggersByWarehouse';
import { useCustomForms } from '@satellite/composables/useCustomForms';
import CustomFormsMultipleTriggerForm from '@satellite/components/elements/CustomFormsMultipleTriggerForm.vue';
import assetVisitService from '@satellite/services/asset-visit-service';

import { useWarehouseStore } from '@/store/useWarehouseStore';
import { useNotify } from '@/composables';

import type { Maybe } from '@nova/core';
import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenter';

const { t } = useI18n();
const { notify } = useNotify();

const props = defineProps<{
  assetId: Maybe<string>;
  assetData: YardAssetDetails | null | undefined;
}>();

const warehouseStore = useWarehouseStore();
const { customFormsComponentsMapping } = useCustomForms();

const { data: assetContainerTriggers } = useAssetContainerTriggersByWarehouse({
  warehouseId: computed(() => warehouseStore.selectedWarehouseId),
  headless: true
});

const assetTypeOptions = Object.values(AssetContainerType);

const editing = reactive({ code: false, type: false, notes: false });
const editValues = reactive({ code: '', type: '', notes: '' });

watch(
  () => props.assetData,
  entity => {
    if (!editing.code) {
      editValues.code = entity?.code ?? '';
    }
    if (!editing.type) {
      editValues.type = entity?.type ?? '';
    }
    if (!editing.notes) {
      editValues.notes = entity?.notes ?? '';
    }
  },
  { immediate: true }
);

const queryClient = useQueryClient();

const { mutateAsync: updateAsset, isPending } = useMutation({
  mutationFn: (data: Record<string, unknown>) => {
    if (!props.assetId) {
      throw new Error('Missing assetId');
    }
    return assetVisitService.updateAssetContainer(props.assetId, data);
  },
  onSuccess: () => {
    if (props.assetId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.assetContainer(props.assetId) });
    }
  },
  onError: () => {
    notify(t('checkAssetPanel.errors.updateFailed'), 'error');
  }
});

async function saveField(field: 'code' | 'type' | 'notes') {
  try {
    await updateAsset({ [field]: editValues[field] });
    editing[field] = false;
  } catch {
    // error notification handled by onError; editing stays open so user can retry
  }
}

function cancelField(field: 'code' | 'type' | 'notes') {
  editValues[field] = props.assetData?.[field] ?? '';
  editing[field] = false;
}
</script>

<template>
  <div class="d-flex flex-column gap-2 pa-4 bg--color-neutral-10">
    <v-card variant="flat" border>
      <editable-key-value
        class="pa-4"
        :label="t('checkAssetPanel.fields.assetCode')"
        :label-props="{ class: 'typography-body-sm-bold' }"
        :value-props="{ class: 'typography-body-md' }"
        :value="assetData?.code ?? PRESENTER_VALUE_FALLBACK"
        :can-edit="Boolean(assetId)"
        always-show-edit-button
        :is-editing="editing.code"
        :save-button-props="{ disabled: isPending || !editValues.code }"
        @edit="editing.code = true"
        @cancel="cancelField('code')"
        @save="saveField('code')">
        <template #editing-value>
          <v-text-field
            v-model="editValues.code"
            variant="outlined"
            density="compact"
            hide-details />
        </template>
      </editable-key-value>
    </v-card>

    <v-card variant="flat" border>
      <editable-key-value
        class="pa-4"
        :label="t('checkAssetPanel.fields.assetType')"
        :label-props="{ class: 'typography-body-sm-bold' }"
        :value-props="{ class: 'typography-body-md' }"
        :value="assetData?.type ?? PRESENTER_VALUE_FALLBACK"
        :can-edit="Boolean(assetId)"
        always-show-edit-button
        :is-editing="editing.type"
        :save-button-props="{ disabled: isPending || !editValues.type }"
        @edit="editing.type = true"
        @cancel="cancelField('type')"
        @save="saveField('type')">
        <template #editing-value>
          <v-select
            v-model="editValues.type as AssetContainerType"
            :items="assetTypeOptions"
            variant="outlined"
            density="compact"
            hide-details />
        </template>
      </editable-key-value>
    </v-card>

    <v-card variant="flat" border>
      <editable-key-value
        class="pa-4"
        :label="t('checkAssetPanel.fields.assetOwner')"
        :label-props="{ class: 'typography-body-sm-bold' }"
        :value-props="{ class: 'typography-body-md' }"
        :value="assetData?.company?.name ?? PRESENTER_VALUE_FALLBACK"
        :can-edit="false" />
    </v-card>

    <v-card variant="flat" border>
      <editable-key-value
        class="pa-4"
        :label="t('checkAssetPanel.fields.notes')"
        :label-props="{ class: 'typography-body-sm-bold' }"
        :value-props="{ class: 'typography-body-md' }"
        :value="assetData?.notes ?? PRESENTER_VALUE_FALLBACK"
        :can-edit="Boolean(assetId)"
        always-show-edit-button
        :is-editing="editing.notes"
        :save-button-props="{ disabled: isPending }"
        @edit="editing.notes = true"
        @cancel="cancelField('notes')"
        @save="saveField('notes')">
        <template #editing-value>
          <v-textarea
            v-model="editValues.notes"
            variant="outlined"
            density="compact"
            hide-details
            rows="3" />
        </template>
      </editable-key-value>
    </v-card>

    <CustomFormsMultipleTriggerForm
      v-if="assetId && assetContainerTriggers?.length"
      :object-id="assetId"
      :warehouse-id="warehouseStore.selectedWarehouseId ?? ''"
      :triggers="assetContainerTriggers"
      :timezone="warehouseStore.selectedWarehouseTimezone ?? 'UTC'"
      :military-time-enabled="false"
      :components-mapping="customFormsComponentsMapping"
      :is-editable="true"
      always-show-edit-button
      field-variant="card"
      variant="list-stacked"
      class="gap-2" />
  </div>
</template>
